import { Row } from '@tanstack/react-table';
import {
  ParsedWbColumnWidth,
  TableCellProps,
  WbColumnMinMaxWidth,
  WbColumnWidth,
  WbColumnWidthType,
} from '../types';
import { isNaN } from 'lodash';
import { MAX_COL_SIZE, MIN_COL_SIZE } from '../constants';

export type GetRowChildren<T> = (row: T) => T[];
export type DepthMap = Map<string, number>;

export const explodeTreeRows = <T>(
  rows: Row<T>[],
  getRowId: (row: T) => string,
  getRowChildren: GetRowChildren<T>,
  expandedRowsIds: string[],
  hasChildren?: (row: T) => boolean,
) => {
  let isAnyRowNested = false;
  const depthMap: DepthMap = new Map();
  const expandedSet = new Set(expandedRowsIds);
  const newRows: T[] = [];

  const dfs = (row: Row<T>, depth = 0) => {
    const original = depth === 0 ? row.original : (row as T);
    const id = getRowId(original);

    if (depth > 0) {
      newRows.push({
        ...row,
        original: row,
      } as T);
    } else {
      newRows.push(row as T);
    }

    depthMap.set(id, depth);
    const children = getRowChildren(original);

    if (children.length > 0) isAnyRowNested = true;
    // For lazy-loaded trees, hasChildren may report a row as expandable even
    // before its children have been fetched (children.length would be 0).
    else if (hasChildren?.(original)) isAnyRowNested = true;

    if (expandedSet.has(id)) {
      children.forEach(r => dfs(r as Row<T>, depth + 1));
    }
  };

  rows.forEach(r => dfs(r));
  // TODO we cast to tanstack Row even though we only provide the "original" field,
  // this logic could be moved to tanstack built in expansion feature instead
  return { rows: newRows as unknown as Row<T>[], isAnyRowNested, depthMap };
};

export function getColumnId<T>(column: TableCellProps<T>): string {
  return column.uniqueFieldId ?? String(column.field).replaceAll('.', '_');
}

/**
 * Parses to a number
 */
export function parseColumnMinWidth(value: WbColumnMinMaxWidth): number {
  const parsed = Number(value);
  if (!isNaN(parsed)) return parsed;
  return MIN_COL_SIZE;
}

/**
 * Parses to a number
 */
export function parseColumnMaxWidth(value: WbColumnMinMaxWidth): number {
  const parsed = Number(value);
  if (!isNaN(parsed)) return parsed;
  return MAX_COL_SIZE;
}

/**
 * Parses to a number or undefined
 */
export function parseColumnAutoMinMaxWidth(
  value: WbColumnMinMaxWidth,
): number | undefined {
  const parsed = Number(value);
  if (!isNaN(parsed)) return parsed;
  return undefined;
}

export function parseColumnWidth(width: WbColumnWidth): ParsedWbColumnWidth {
  if (width === undefined) {
    return { type: WbColumnWidthType.NOT_DEFINED };
  }
  if (width === 'auto') {
    return { type: WbColumnWidthType.AUTO };
  }
  if (typeof width === 'number') {
    return { type: WbColumnWidthType.FIXED, value: width };
  }
  if (typeof width === 'string') {
    if (width.endsWith('%')) {
      const num = Number(width.slice(0, -1));
      if (!isNaN(num)) {
        return { type: WbColumnWidthType.PERCENT, value: num };
      }
    } else if (width.endsWith('px')) {
      const num = Number(width.slice(0, -2));
      if (!isNaN(num)) {
        return { type: WbColumnWidthType.FIXED, value: num };
      }
    } else {
      const num = Number(width);
      if (!isNaN(num)) {
        return { type: WbColumnWidthType.FIXED, value: num };
      }
    }
  }
  return { type: WbColumnWidthType.NOT_DEFINED };
}
